import { BaseGenerator } from './BaseGenerator.js';
import { ZONE } from './ZoneTypes.js';
import { MapPipeline } from './MapPipeline.js';

/**
 * Gas station generator.
 * Produces a convenience store with pump islands, parking lot, and road.
 * Interior BSP-splits into store, backroom, and restroom.
 *
 * Options:
 * - quality: 'draft' | 'standard' | 'high'
 *
 * Ported from OMN's generateGasStation.
 */
export class GasStationGenerator extends BaseGenerator {
  generate(content) {
    const quality = this.options.quality || 'draft';
    const tiles = content.tiles || GasStationGenerator.DEFAULT_TILES;
    const opts = { ...GasStationGenerator.DEFAULT_OPTIONS, ...this.options };

    const storeW = this.rng.nextInt(12, 16);
    const storeH = this.rng.nextInt(8, 12);

    const hl = Math.floor((this.width - storeW) / 2);
    const ht = Math.floor(this.height / 2) - storeH - 2;
    const hr = hl + storeW;
    const hb = ht + storeH;

    const roadTop = this.height - 7;
    const roadHeight = 3;
    const sidewalkN = roadTop - 1;
    const sidewalkS = roadTop + roadHeight;
    const parkingMargin = 3;

    const ground = this.createGrid(this.width, this.height, ZONE.GRASS);
    const objects = this.createGrid(this.width, this.height, ZONE.EMPTY);
    const overhead = this.createGrid(this.width, this.height, ZONE.EMPTY);

    // 1. Grass
    this._fillGrass(ground);
    this._addGrassVariation(ground, 5);

    // 2. Parking lot (asphalt)
    this.fillRect(ground, hl - parkingMargin, hb, hr + parkingMargin - 1, roadTop - 1, ZONE.ASPHALT);
    this.fillRect(ground, hl - parkingMargin, ht - 2, hr + parkingMargin - 1, ht - 1, ZONE.ASPHALT);

    // 3. Road + sidewalks
    this._fillRoad(ground, roadTop, roadHeight);
    this._fillSidewalkRow(ground, sidewalkN);
    this._fillSidewalkRow(ground, sidewalkS);

    // 4. Building interior — manual BSP: store (left 60%), back area (right 40%)
    const splitCol = hl + 1 + Math.floor((storeW - 2) * 0.6);
    const rightH = storeH - 2;
    const splitRow = ht + 1 + Math.floor(rightH * 0.6);

    // Store floor
    this.fillRect(ground, hl + 1, ht + 1, splitCol - 1, ht + storeH - 2, ZONE.STORE);
    // Backroom floor
    this.fillRect(ground, splitCol, ht + 1, hr - 2, splitRow - 1, ZONE.BACKROOM);
    // Restroom floor
    this.fillRect(ground, splitCol, splitRow, hr - 2, ht + storeH - 2, ZONE.RESTROOM);

    // 5. Perimeter walls
    this._placePerimeterWalls(objects, hl, ht, hr, hb);

    // 6. Interior walls — vertical between store and back area
    for (let r = ht + 1; r < hb - 1; r++) {
      this.setCell(objects, splitCol, r, ZONE.WALL_INT);
    }
    // Doorframe in vertical wall
    const vDoorRow = Math.floor((ht + 1 + hb - 1) / 2);
    this.setCell(objects, splitCol, vDoorRow, ZONE.DOORFRAME);
    if (vDoorRow + 1 < hb - 1) this.setCell(objects, splitCol, vDoorRow + 1, ZONE.DOORFRAME);

    // Horizontal wall between backroom and restroom
    for (let c = splitCol; c < hr - 1; c++) {
      this.setCell(objects, c, splitRow, ZONE.WALL_INT);
    }
    const hDoorCol = Math.floor((splitCol + hr - 1) / 2);
    this.setCell(objects, hDoorCol, splitRow, ZONE.DOORFRAME);

    // 7. Front door (south wall, store side)
    const frontDoorCol = Math.floor((hl + splitCol) / 2);
    this.setCell(objects, frontDoorCol, hb - 1, ZONE.DOOR);
    this.setCell(ground, frontDoorCol, hb - 1, ZONE.STORE);

    // Side door for restroom (right wall)
    const sideRow = Math.floor(splitRow + (ht + storeH - 2 - splitRow) / 2);
    if (sideRow > ht && sideRow < hb - 1) {
      this.setCell(objects, hr - 1, sideRow, ZONE.DOOR);
      this.setCell(ground, hr - 1, sideRow, ZONE.RESTROOM);
    }

    // 8. Windows (front wall, store side only)
    for (let c = hl + 2; c < splitCol - 1; c += 3) {
      if (this.getCell(objects, c, hb - 1) === ZONE.WALL_EXT) {
        this.setCell(objects, c, hb - 1, ZONE.WINDOW);
      }
    }

    // 9. Gas pump islands
    const pumpCount = this.rng.nextInt(2, 3);
    const pumpRow = hb + 3;
    for (let i = 0; i < pumpCount; i++) {
      const col = hl + i * 4 + 2;
      if (col < this.width - 3) {
        this.setCell(objects, col, pumpRow, ZONE.PUMP_ISLAND);
        this.setCell(objects, col, pumpRow + 1, ZONE.PUMP_ISLAND);
      }
    }

    // 10. Dumpster behind building
    this.setCell(objects, hr - 2, ht - 1, ZONE.CRATE);
    this.setCell(objects, hr - 4, ht - 1, ZONE.BARREL);

    // 11. Cars in parking lot
    this.setCell(objects, hl - 2, hb + 2, ZONE.CRATE);
    this.setCell(objects, hr + 2, hb + 2, ZONE.CRATE);
    this._placeRoadCars(objects, roadTop, hl - parkingMargin, hr + parkingMargin, this.rng.nextInt(1, 2));

    // 13. Trees around edges
    this._scatterYardDecorations(objects, hl - parkingMargin, ht - 2, hr + parkingMargin, roadTop,
      this.rng.nextInt(3, 5), this.rng.nextInt(2, 3));

    // Build room metadata
    const rooms = [
      { x: hl + 1, y: ht + 1, width: splitCol - hl - 1, height: storeH - 2, type: 'store' },
      { x: splitCol + 1, y: ht + 1, width: hr - 1 - splitCol - 1, height: splitRow - ht - 1, type: 'backroom' },
      { x: splitCol + 1, y: splitRow + 1, width: hr - 1 - splitCol - 1, height: ht + storeH - 2 - splitRow, type: 'restroom' },
    ];

    // Room decorations
    this._decorateRooms(objects, rooms);

    if (quality !== 'draft') {
      const pipeline = MapPipeline.createDefault(this.rng, { tileMeta: this.options.tileMeta });
      if (quality !== 'high') pipeline.removePass('decoration');

      const pipelineOptions = {
        tileMapping: this._buildTileMapping(tiles),
        floorTileIds: [ZONE.FLOOR, ZONE.DOOR, ZONE.STORE, ZONE.BACKROOM, ZONE.RESTROOM],
        corridorTileId: ZONE.FLOOR,
        cleanup: { minPatchSize: 0, smoothIterations: 0 },
      };

      const result = pipeline.run({
        ground, objects, overhead,
        zones: ground,
        width: this.width, height: this.height,
        metadata: { rooms },
      }, pipelineOptions);

      const finalGround = this._resolveZones(result.ground, tiles);
      const finalObjects = this._resolveObjectZones(result.objects, tiles);
      const finalOverhead = result.overhead.map(row => [...row]);

      if (tiles._tileLayers) {
        this.redistributeByLayer(
          { ground: finalGround, objects: finalObjects, overhead: finalOverhead },
          tiles._tileLayers,
        );
      }

      return this.buildTMJ({
        layers: [
          { name: 'Ground', data: this.flattenGrid(finalGround) },
          { name: 'Objects', data: this.flattenGrid(finalObjects) },
          { name: 'Overhead', data: this.flattenGrid(finalOverhead) },
        ],
        tilesets: content.tilesets || [],
        properties: {
          generator: { type: 'string', value: 'gas_station' },
          rooms: { type: 'string', value: JSON.stringify(rooms) },
        },
      });
    }

    const draftGround = this._resolveZones(ground, tiles);
    const draftObjects = this._resolveObjectZones(objects, tiles);
    const draftOverhead = overhead.map(row => [...row]);

    if (tiles._tileLayers) {
      this.redistributeByLayer(
        { ground: draftGround, objects: draftObjects, overhead: draftOverhead },
        tiles._tileLayers,
      );
    }

    return this.buildTMJ({
      layers: [
        { name: 'Ground', data: this.flattenGrid(draftGround) },
        { name: 'Objects', data: this.flattenGrid(draftObjects) },
        { name: 'Overhead', data: this.flattenGrid(draftOverhead) },
      ],
      tilesets: content.tilesets || [],
      properties: {
        generator: { type: 'string', value: 'gas_station' },
        rooms: { type: 'string', value: JSON.stringify(rooms) },
      },
    });
  }

  // ---- Helpers ----

  _decorateRooms(objects, rooms) {
    for (const room of rooms) {
      if (!room.type || room.width < 3 || room.height < 3) continue;
      if (room.type === 'store') {
        // Counters along back wall
        for (let x = room.x + 1; x < room.x + room.width - 1; x++) {
          if (this.rng.chance(0.5)) {
            this.setCell(objects, x, room.y + 1, ZONE.COUNTER);
          }
        }
        // Shelving (bookshelf as stand-in)
        const shelfCount = this.rng.nextInt(1, 3);
        for (let i = 0; i < shelfCount; i++) {
          const x = this.rng.nextInt(room.x + 1, room.x + room.width - 2);
          const y = this.rng.nextInt(room.y + 2, room.y + room.height - 2);
          if (this.getCell(objects, x, y) === 0 || this.getCell(objects, x, y) === ZONE.EMPTY) {
            this.setCell(objects, x, y, ZONE.BOOKSHELF);
          }
        }
      } else if (room.type === 'backroom') {
        // Crates and barrels
        const itemCount = this.rng.nextInt(1, 3);
        for (let i = 0; i < itemCount; i++) {
          const x = this.rng.nextInt(room.x + 1, room.x + room.width - 2);
          const y = this.rng.nextInt(room.y + 1, room.y + room.height - 2);
          if (this.getCell(objects, x, y) === 0 || this.getCell(objects, x, y) === ZONE.EMPTY) {
            this.setCell(objects, x, y, this.rng.pick([ZONE.CRATE, ZONE.BARREL]));
          }
        }
      } else if (room.type === 'restroom') {
        // Sparse — just a counter
        if (room.width >= 3 && room.height >= 3) {
          this.setCell(objects, room.x + 1, room.y + 1, ZONE.COUNTER);
        }
      }
    }
  }

  _fillGrass(ground) {
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        ground[y][x] = ZONE.GRASS;
      }
    }
  }

  _fillRoad(ground, roadTop, roadHeight) {
    for (let r = roadTop; r < roadTop + roadHeight && r < this.height; r++) {
      for (let c = 0; c < this.width; c++) {
        this.setCell(ground, c, r, ZONE.ROAD);
      }
    }
  }

  _fillSidewalkRow(ground, row) {
    if (row < 0 || row >= this.height) return;
    for (let c = 0; c < this.width; c++) {
      this.setCell(ground, c, row, ZONE.SIDEWALK);
    }
  }

  _placePerimeterWalls(objects, hl, ht, hr, hb) {
    for (let c = hl; c < hr; c++) {
      this.setCell(objects, c, ht, ZONE.WALL_EXT);
      this.setCell(objects, c, hb - 1, ZONE.WALL_EXT);
    }
    for (let r = ht; r < hb; r++) {
      this.setCell(objects, hl, r, ZONE.WALL_EXT);
      this.setCell(objects, hr - 1, r, ZONE.WALL_EXT);
    }
  }

  _scatterYardDecorations(objects, hl, ht, hr, hb, treeCount, bushCount) {
    const margin = 3;
    const placed = new Set();
    const tryPlace = (zone, count) => {
      let n = 0;
      for (let attempt = 0; attempt < count * 10 && n < count; attempt++) {
        const c = this.rng.nextInt(margin, this.width - margin - 1);
        const r = this.rng.nextInt(margin, this.height - margin - 1);
        if (c >= hl - 1 && c < hr + 1 && r >= ht - 1 && r < hb + 1) continue;
        const key = `${c},${r}`;
        if (placed.has(key)) continue;
        placed.add(key);
        this.setCell(objects, c, r, zone);
        n++;
      }
    };
    tryPlace(ZONE.TREE, treeCount);
    tryPlace(ZONE.BUSH, bushCount);
  }

  _placeRoadCars(objects, roadTop, hl, hr, count) {
    for (let i = 0; i < count; i++) {
      const c = this.rng.nextInt(4, this.width - 4);
      if (c >= hl - 1 && c <= hr + 1) continue;
      this.setCell(objects, c, roadTop + 1, ZONE.CRATE);
    }
  }

  _buildTileMapping(tiles) {
    const mapping = {};
    mapping[ZONE.GRASS] = { center: tiles.grass || 1 };
    mapping[ZONE.ROAD] = { center: tiles.road || tiles.asphalt || 1 };
    mapping[ZONE.SIDEWALK] = { center: tiles.sidewalk || 1 };
    mapping[ZONE.ASPHALT] = { center: tiles.asphalt || 1 };
    mapping[ZONE.WALL_EXT] = { center: tiles.wallExt || tiles.wall };
    mapping[ZONE.WALL_INT] = { center: tiles.wallInt || tiles.wall };
    mapping[ZONE.FLOOR] = { center: tiles.floor };
    mapping[ZONE.STORE] = { center: tiles.floorStore || tiles.floor };
    mapping[ZONE.BACKROOM] = { center: tiles.floorBackroom || tiles.floor };
    mapping[ZONE.RESTROOM] = { center: tiles.floorRestroom || tiles.floor };
    mapping[ZONE.DOOR] = { center: tiles.door || tiles.floor };
    mapping[ZONE.DOORFRAME] = { center: tiles.doorframe || tiles.floor };
    return mapping;
  }

  _resolveZones(grid, tiles) {
    const mapping = {
      [ZONE.EMPTY]: 0,
      [ZONE.GRASS]: tiles.grass || 1,
      [ZONE.ROAD]: tiles.road || tiles.asphalt || 1,
      [ZONE.SIDEWALK]: tiles.sidewalk || 1,
      [ZONE.ASPHALT]: tiles.asphalt || 1,
      [ZONE.WALL]: tiles.wall,
      [ZONE.FLOOR]: tiles.floor,
      [ZONE.STORE]: tiles.floorStore || tiles.floor,
      [ZONE.BACKROOM]: tiles.floorBackroom || tiles.floor,
      [ZONE.RESTROOM]: tiles.floorRestroom || tiles.floor,
      [ZONE.DOOR]: tiles.door || tiles.floor,
    };
    return grid.map(row => row.map(z => mapping[z] !== undefined ? mapping[z] : z));
  }

  _resolveObjectZones(grid, tiles) {
    const mapping = {
      [ZONE.EMPTY]: 0,
      [ZONE.WALL_EXT]: tiles.wallExt || tiles.wall,
      [ZONE.WALL_INT]: tiles.wallInt || tiles.wall,
      [ZONE.WINDOW]: tiles.window || 0,
      [ZONE.DOOR]: tiles.door || tiles.floor,
      [ZONE.DOORFRAME]: tiles.doorframe || tiles.floor,
      [ZONE.PUMP_ISLAND]: tiles.pumpIsland || tiles.barrel || 0,
      [ZONE.FENCE]: tiles.fence || 0,
      [ZONE.TREE]: tiles.tree || 0,
      [ZONE.BUSH]: tiles.bush || 0,
      [ZONE.CRATE]: tiles.crate || 0,
      [ZONE.BARREL]: tiles.barrel || 0,
      [ZONE.COUNTER]: tiles.counter || 0,
      [ZONE.BOOKSHELF]: tiles.bookshelf || 0,
    };
    return grid.map(row => row.map(z => mapping[z] !== undefined ? mapping[z] : z));
  }

  static DEFAULT_TILES = {
    wall: 5,
    wallExt: 5,
    wallInt: 5,
    floor: 6,
    door: 7,
    doorframe: 6,
    grass: 1,
    road: 16,
    asphalt: 16,
    sidewalk: 8,
    window: 5,
    fence: 23,
    tree: 10,
    bush: 11,
    crate: 41,
    barrel: 40,
    pumpIsland: 40,
    counter: 42,
    bookshelf: 43,
    floorStore: 6,
    floorBackroom: 6,
    floorRestroom: 6,
  };

  static DEFAULT_OPTIONS = {
    quality: 'draft',
  };

  static getEmittedZones() {
    return [
      ZONE.GRASS, ZONE.ROAD, ZONE.SIDEWALK, ZONE.ASPHALT, ZONE.WALL_EXT, ZONE.WALL_INT, ZONE.WALL, ZONE.DOOR, ZONE.DOORFRAME, ZONE.WINDOW, ZONE.FENCE, ZONE.FLOOR, ZONE.STORE, ZONE.BACKROOM, ZONE.RESTROOM, ZONE.PUMP_ISLAND, ZONE.TREE, ZONE.BUSH, ZONE.CRATE, ZONE.BARREL, ZONE.COUNTER, ZONE.BOOKSHELF,
    ];
  }
}
